import React, { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../store/actions/userActions";
import { BASE_URL } from "../../../constants";
import wordmark from "../../../assets/brand/cache-wordmark.svg";
import { initialsOf } from "../../ForBrands/brandDealUtils";
import { useFitScale, fitScaleStyle } from "../../ForBrands/useFitScale";
import { isTeamGamesPath } from "../teamPaths";
import "../../ForBrands/brandMode.css";

// Admin Mode shell: the same 72px topbar, tab bar and fit-to-width frame the
// Brand, Athlete and Coach V2 shells use, over the admin pages (/users and
// every /admin page, Codes included). The tab state comes from the path
// alone (aria-current and the orange underline together), so a page that is
// no tab, such as /users/password, lights up none.

const TABS = [
  { path: "/users", label: "Users", match: ["/users"], admin: false },
  { path: "/admin/team-management", label: "Teams", match: ["/admin/team-management"], admin: true },
  { path: "/cacheshare-tool", label: "CacheShare", match: ["/cacheshare-tool"], admin: true },
  { path: "/admin/data-sync", label: "Data & Compute", match: ["/admin/data-sync"], admin: true },
  { path: "/admin/analytics", label: "Analytics", match: ["/admin/analytics"], admin: true },
  { path: "/admin/emails", label: "Emails", match: ["/admin/emails"], admin: true },
  { path: "/admin/payments", label: "Payments", match: ["/admin/payments"], admin: true },
  { path: "/admin/api-keys", label: "API keys", match: ["/admin/api-keys"], admin: true },
];

const MORE_LINKS = [
  { to: "/admin/promocodes", label: "Promo codes" },
  { to: "/admin/dependencies", label: "Dependencies" },
  { to: "/admin/codes", label: "Codes" },
  { to: "/admin/logs", label: "Logs" },
];

// The account's own password page, inside the shell for every staff role.
export const ADMIN_PASSWORD_PATH = "/users/password";

// Pages under a tab's path that belong to no tab: the avatar menu opens
// them, like the More menu pages (/admin/promocodes, /admin/codes,
// /admin/logs), so no tab lights up there.
const NON_TAB_PATHS = [ADMIN_PASSWORD_PATH];

const tabMatches = (tab, pathname) => (
  !NON_TAB_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  && tab.match.some((m) => pathname === m || pathname.startsWith(`${m}/`))
);

export const PLAYER_INDEX_PATH = "/business/dashboard";

// The page area. Admin pages sit inside the shell gutter. A full-bleed page
// (the CacheShare™ tool, Figma 03 admin view, and a partner team's game
// stats sheet, whose stat columns need the width) fills the window under the
// tab bar: main stretches to the bottom and the page stretches to fill main.
// From lg up, main pads 24px on the sides, and a side rail (an <aside> at
// the page's top level) sits flush left and runs down the page. Below lg the
// rail stacks on top, so the usual side gutter applies. The bottom padding
// stays.
const MAIN_GUTTER = "px-5 pb-[70px] sm:px-8 lg:px-12 min-[1440px]:px-[122px]";
const MAIN_FULL_BLEED = "flex flex-1 flex-col px-5 pb-[70px] sm:px-8 lg:px-6 lg:has-[>*>aside]:pl-0 *:flex-1";
const FULL_BLEED_PATHS = ["/cacheshare-tool"];

// Where the wordmark leads. Admins and Cache AI staff go to the Player Index,
// their landing after log in. A coach (not staff) goes to Coach Mode home.
// Interns stay on Users, because the Player Index does not admit them.
export const adminHomeFor = (permissions = []) => {
  const perms = Array.isArray(permissions) ? permissions : [];
  if (perms.includes("IS_ADMIN") || perms.includes("IS_CACHE_AI")) return PLAYER_INDEX_PATH;
  if (perms.includes("IS_COACH")) return "/coach/home";
  return "/users";
};

const HOME_LABEL = {
  [PLAYER_INDEX_PATH]: "Player Index",
  "/coach/home": "Coach home",
  "/users": "Admin home",
};

// Staff see when live stats are paused (the Cerebro breaker keeps live calls
// off, see backend users/cerebro.py): one cheap read of the breaker (no
// probe), a moment after the shell opens and every two minutes after that,
// plus once more when a pause should be over (a 429 pauses calls only for
// the seconds Cerebro asks, often under a minute).
export const BREAKER_STATUS_URL = `${BASE_URL}/api/users/admin/dependencies/health/?only=breakers`;
const BREAKER_FIRST_CHECK_MS = 1500;
const BREAKER_CHECK_EVERY_MS = 120000;
const BREAKER_RECHECK_MIN_MS = 10000;
const BREAKER_RECHECK_SLACK_MS = 1500;

// The breaker pauses live calls only while its window runs (`paused`); an
// open breaker past its window pauses nothing, the next call checks the feed.
const isPaused = (breaker, now) => {
  if (!breaker || !breaker.open) return false;
  if (typeof breaker.paused === "boolean") return breaker.paused;
  const until = breaker.nextProbeAt ? new Date(breaker.nextProbeAt) : null;
  return !until || Number.isNaN(until.getTime()) || until > now;
};

export const pausedUntilText = (breaker, now = new Date()) => {
  if (!isPaused(breaker, now)) return "";
  const until = breaker.nextProbeAt ? new Date(breaker.nextProbeAt) : null;
  if (!until || Number.isNaN(until.getTime())) return "Live stats paused";
  const time = until.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const day = until.toDateString() === now.toDateString()
    ? "" : `${until.toLocaleDateString([], { month: "short", day: "numeric" })}, `;
  return `Live stats paused until ${day}${time}`;
};

// Milliseconds until the breaker is read again because its pause should be
// over (never sooner than BREAKER_RECHECK_MIN_MS, never later than the
// regular check), or null while nothing is paused.
export const breakerRecheckMs = (breaker, nowMs = Date.now()) => {
  if (!isPaused(breaker, new Date(nowMs))) return null;
  const until = breaker.nextProbeAt ? new Date(breaker.nextProbeAt).getTime() : NaN;
  if (Number.isNaN(until)) return null;
  return Math.min(BREAKER_CHECK_EVERY_MS, Math.max(BREAKER_RECHECK_MIN_MS, until - nowMs + BREAKER_RECHECK_SLACK_MS));
};

const useLiveStatsPause = (enabled) => {
  const token = useSelector((state) => state.auth?.token);
  const [breaker, setBreaker] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    let recheck = null;
    const check = () => {
      if (typeof fetch !== "function") return;
      const auth = token || localStorage.getItem("token");
      fetch(BREAKER_STATUS_URL, { headers: { Authorization: `Bearer ${auth}` } })
        .then((r) => (r && r.ok ? r.json() : null))
        .then((d) => {
          if (!alive) return;
          const next = d?.breakers?.cerebro || null;
          setBreaker(next);
          clearTimeout(recheck);
          const wait = breakerRecheckMs(next);
          recheck = wait == null ? null : setTimeout(check, wait);
        })
        .catch(() => {});
    };
    const first = setTimeout(check, BREAKER_FIRST_CHECK_MS);
    const every = setInterval(check, BREAKER_CHECK_EVERY_MS);
    return () => { alive = false; clearTimeout(first); clearInterval(every); clearTimeout(recheck); };
  }, [enabled, token]);
  return breaker;
};

const displayName = (user) => {
  const first = user?.firstName || (user?.name || "").split(" ")[0] || "";
  const last = user?.lastName || user?.surname || ((user?.name || "").split(" ").slice(1).join(" ") || "");
  const full = `${first} ${last}`.trim();
  return full || user?.email || "Admin";
};

const AvatarMenu = ({ user, isAdmin, showIndex }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const name = displayName(user);
  const initials = initialsOf(name.split(" ")[0], name.split(" ").slice(1).join(" "));

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("myEmail");
    localStorage.removeItem("athleteEmail");
    localStorage.removeItem("expiresAt");
    dispatch(logout());
    navigate("/login");
  };

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} className="flex items-center gap-4">
        <span className="hidden bm-label-l text-bm-ink sm:inline">{name}</span>
        {user?.avatar ? (
          <img src={user.avatar} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-bm-orange bm-label-xs text-white">{initials}</span>
        )}
      </button>
      {open && (
        <div role="menu" className="bm-fade-in absolute right-0 top-11 z-40 w-60 overflow-hidden rounded-[12px] border border-bm-border bg-white py-2 shadow-xl">
          <div className="px-4 pb-2 pt-1">
            <div className="bm-label-l text-bm-ink">{name}</div>
            <div className="truncate bm-body-s text-bm-muted">{user?.email}</div>
          </div>
          <div className="border-t border-bm-border" />
          {showIndex && (
            <>
              <Link to={PLAYER_INDEX_PATH} className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
                Player Index
              </Link>
              <div className="border-t border-bm-border" />
            </>
          )}
          {isAdmin && MORE_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
              {l.label}
            </Link>
          ))}
          {isAdmin && <div className="border-t border-bm-border" />}
          <Link to={ADMIN_PASSWORD_PATH} className="block px-4 py-2 bm-body-m text-bm-ink hover:bg-bm-subtle">
            Password
          </Link>
          <div className="border-t border-bm-border" />
          <button type="button" onClick={handleLogout} className="block w-full px-4 py-2 text-left bm-body-m text-bm-ink hover:bg-bm-subtle">
            Log out
          </button>
        </div>
      )}
    </div>
  );
};

const AdminModeLayout = () => {
  const location = useLocation();
  const user = useSelector((state) => state.user.user || {});
  const permissions = user.permissions || [];
  const isAdmin = permissions.includes("IS_ADMIN");
  const paused = pausedUntilText(useLiveStatsPause(isAdmin || permissions.includes("IS_CACHE_AI")));
  const home = adminHomeFor(permissions);
  const showIndex = home === PLAYER_INDEX_PATH;
  const fit = useFitScale();
  const tabs = TABS.filter((t) => isAdmin || !t.admin);
  const activeTab = tabs.find((t) => tabMatches(t, location.pathname)) || null;
  // A team's game stats sheet runs full bleed as well. It sits under the
  // Teams tab's path, so Teams stays lit there.
  const fullBleed = FULL_BLEED_PATHS.some((p) => location.pathname.startsWith(p)) || isTeamGamesPath(location.pathname);

  return (
    <div className={`bm-root w-full min-w-0 max-w-full overflow-x-clip bg-white${fullBleed ? " flex flex-col" : ""}`} style={fitScaleStyle(fit)}>
      <header className="bg-white">
        <div className="flex h-[72px] w-full items-center justify-between px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
          <Link to={home} className="flex items-center" aria-label={HOME_LABEL[home] || "Admin home"}>
            <img src={wordmark} alt="CACHE" className="h-[19px] w-[95px]" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden bm-overline md:inline">{isAdmin ? "Admin Mode" : "Staff"}</span>
            <span className="h-5 w-px bg-bm-border" />
            <AvatarMenu user={user} isAdmin={isAdmin} showIndex={showIndex} />
          </div>
        </div>
        {/* The tabs tighten below xl and wrap onto more rows on a narrow
            screen instead of scrolling the strip sideways. */}
        <nav className="border-b border-bm-border">
          <div className="flex min-h-[51px] w-full flex-wrap items-stretch px-5 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
            {tabs.map((tab) => {
              const active = tab === activeTab;
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  aria-current={active ? "page" : undefined}
                  className={`flex shrink-0 items-center gap-2 px-2 py-3 font-archivo text-[15px] font-bold leading-5 lg:px-4 xl:px-6 ${
                    active ? "border-b-2 border-bm-orange text-bm-orange" : "text-bm-muted hover:text-bm-ink"
                  }`}
                >
                  {tab.label}
                </Link>
              );
            })}
          </div>
        </nav>
        {paused && (
          <div className="border-b border-bm-border bg-bm-subtle" aria-live="polite" data-testid="live-stats-paused">
            <div className="flex w-full flex-wrap items-center gap-2 px-5 py-2 sm:px-8 lg:px-12 min-[1440px]:px-[122px]">
              <span className="h-2 w-2 shrink-0 rounded-full bg-bm-critical" />
              <span className="bm-body-s text-bm-ink">{paused}. Pages show saved stats until the feed answers again.</span>
              {isAdmin && (
                <Link to="/admin/dependencies" className="bm-label-m text-bm-orange hover:text-[#e63d00]">Dependencies</Link>
              )}
            </div>
          </div>
        )}
      </header>
      <main className={`w-full min-w-0 max-w-full ${fullBleed ? MAIN_FULL_BLEED : MAIN_GUTTER}`}>
        <Outlet />
      </main>
    </div>
  );
};

export default AdminModeLayout;
